import type { GlossaryTerm } from "@/types/glossary";
import { create } from "zustand";
import { listGlossaryTerms } from "@/api/generated/glossaries/glossaries";
import { listPreviewGlossaryTerms } from "@/api/generated/preview/preview";
import { useCourseDetailStore } from "@/stores/courseDetailStore";

interface CourseGlossaryState {
  terms: GlossaryTerm[];
  loading: boolean;
  error: string | null;
  loadedCourseId: string | null;
  // The request currently running, shared with everyone who asks for the same
  // course while it is in flight.
  inFlight: Promise<void> | null;
  inFlightCourseId: string | null;
  // UI state for the glossary slideover, driven from inline term popovers.
  isOpen: boolean;
  focusTermId: string | null;
  fetchGlossary: (courseExternalId: string) => Promise<void>;
  setGlossaryOpen: (open: boolean) => void;
  openGlossary: (focusTermId?: string) => void;
}

export const useCourseGlossaryStore = create<CourseGlossaryState>(
  (set, get) => ({
    terms: [],
    loading: false,
    error: null,
    loadedCourseId: null,
    inFlight: null,
    inFlightCourseId: null,
    isOpen: false,
    focusTermId: null,

    fetchGlossary: async (courseExternalId: string) => {
      const state = get();
      // Skip refetch when the same course's glossary is already loaded.
      if (
        state.loadedCourseId === courseExternalId &&
        !state.error &&
        !state.loading
      ) {
        return;
      }

      // Share the running request instead of starting a second one: the course
      // content and the slideover both ask on mount, and React's StrictMode
      // mounts each of them twice in development.
      if (state.inFlight && state.inFlightCourseId === courseExternalId) {
        return state.inFlight;
      }

      const { enrollmentId } = useCourseDetailStore.getState();
      const isPreview = enrollmentId === null;

      const request = (async () => {
        try {
          const response = isPreview
            ? await listPreviewGlossaryTerms(courseExternalId)
            : await listGlossaryTerms(enrollmentId);
          set({
            terms: response.terms,
            loading: false,
            loadedCourseId: courseExternalId,
            inFlight: null,
            inFlightCourseId: null,
          });
        } catch (error) {
          set({
            loading: false,
            error:
              error instanceof Error
                ? error.message
                : "Failed to fetch glossary",
            inFlight: null,
            inFlightCourseId: null,
          });
        }
      })();

      // Runs before any of the request's own writes: everything above only
      // issues the call and then awaits, so this lands first.
      set({
        loading: true,
        error: null,
        inFlight: request,
        inFlightCourseId: courseExternalId,
      });

      return request;
    },

    setGlossaryOpen: (open: boolean) =>
      set(open ? { isOpen: true } : { isOpen: false, focusTermId: null }),

    openGlossary: (focusTermId?: string) =>
      set({ isOpen: true, focusTermId: focusTermId ?? null }),
  })
);
